import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import AddIcon from "@mui/icons-material/Add";
import RestoreIcon from "@mui/icons-material/Restore";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

import { StoreState } from "../../redux/configure-store";
import { Task, Project } from "./taskboard-reducer";
import {
  fetchTaskBoard,
  createTask,
  updateTask,
  deleteTask,
  archiveTask,
  createProject,
  deleteProject,
  unarchiveProject,
  renameProject,
} from "./taskboard-actions";
import TaskCard from "./TaskCard";
import TaskModal from "./TaskModal";
import PageTitle from "../components/PageTitle";

const STAGES: { id: Task["stage"]; label: string }[] = [
  { id: "todo", label: "To Do" },
  { id: "in_progress", label: "In Progress" },
  { id: "blocked", label: "Blocker" },
  { id: "done", label: "Done" },
];

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

const STAGE_COLORS: Record<string, string> = {
  todo: "#6366f1",
  in_progress: "#3b82f6",
  blocked: "#ef4444",
  done: "#22c55e",
};

const STAGE_BG: Record<string, string> = {
  todo: "#eef0ff",
  in_progress: "#eff6ff",
  blocked: "#fff1f2",
  done: "#f0fdf4",
};

const PROJECT_COLORS = [
  "#f59e0b", "#10b981", "#3b82f6", "#ec4899",
  "#8b5cf6", "#0ea5e9", "#14b8a6", "#f43f5e", "#84cc16", "#6366f1",
];

function projectColor(id: number) {
  return PROJECT_COLORS[id % PROJECT_COLORS.length];
}

// Droppable column wrapper so empty columns accept drops
function DroppableColumn({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef } = useDroppable({ id });
  return <Box ref={setNodeRef} sx={{ flex: 1, minHeight: 80 }}>{children}</Box>;
}

interface Props {
  tasks: Task[];
  projects: Project[];
  users: any[];
  fetchTaskBoard: (includeArchivedTasks?: boolean, includeArchivedProjects?: boolean) => void;
  createTask: (task: any) => Promise<any>;
  updateTask: (id: number, fields: any) => Promise<any>;
  deleteTask: (id: number) => void;
  archiveTask: (id: number) => void;
  createProject: (name: string) => Promise<any>;
  deleteProject: (id: number) => Promise<any>;
  unarchiveProject: (id: number) => Promise<any>;
  renameProject: (id: number, name: string) => Promise<any>;
}

function TaskBoardPage({
  tasks,
  projects,
  users,
  fetchTaskBoard: _fetch,
  createTask: _createTask,
  updateTask: _updateTask,
  deleteTask: _deleteTask,
  archiveTask: _archiveTask,
  createProject: _createProject,
  deleteProject: _deleteProject,
  unarchiveProject: _unarchiveProject,
  renameProject: _renameProject,
}: Props) {
  // Local task state for optimistic DnD — only syncs from Redux when not dragging
  const [localTasks, setLocalTasks] = useState<Task[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [defaultStage, setDefaultStage] = useState<Task["stage"]>("todo");
  const [projectFilter, setProjectFilter] = useState<number[]>([]);
  const [stageFilter, setStageFilter] = useState<Task["stage"][]>([]);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [renamingProject, setRenamingProject] = useState<Project | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => { _fetch(showArchived, showArchivedProjects); }, [_fetch, showArchived, showArchivedProjects]);

  // Only sync local state from Redux when not mid-drag
  useEffect(() => {
    if (activeId === null) setLocalTasks(tasks);
  }, [tasks, activeId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const activeTask = activeId !== null ? localTasks.find((t) => t.id === activeId) ?? null : null;

  const taskCountByProject = tasks.reduce((acc, t) => {
    acc[t.projectId] = (acc[t.projectId] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);

  const visibleTasks = localTasks.filter((t) => {
    if (projectFilter.length > 0 && !projectFilter.includes(t.projectId)) return false;
    if (stageFilter.length > 0 && !stageFilter.includes(t.stage)) return false;
    if (searchText && !t.title.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  const tasksForStage = (stage: Task["stage"]) =>
    visibleTasks.filter((t) => t.stage === stage).sort((a, b) => {
      const pa = a.priority ? (PRIORITY_ORDER[a.priority] ?? 3) : 3;
      const pb = b.priority ? (PRIORITY_ORDER[b.priority] ?? 3) : 3;
      if (pa !== pb) return pa - pb;
      if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
      if (a.dueDate) return -1;
      if (b.dueDate) return 1;
      return a.position - b.position;
    });

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const draggedIdx = localTasks.findIndex((t) => t.id === active.id);
    if (draggedIdx === -1) return;
    const dragged = localTasks[draggedIdx];

    const overTask = localTasks.find((t) => t.id === over.id);
    const overStageCol = STAGES.find((s) => s.id === over.id);
    const targetStage = overTask?.stage ?? overStageCol?.id;
    if (!targetStage) return;

    setLocalTasks((prev) => {
      const updated = prev.map((t) =>
        t.id === dragged.id ? { ...t, stage: targetStage } : t
      );
      if (overTask) {
        const fromIdx = updated.findIndex((t) => t.id === dragged.id);
        const toIdx = updated.findIndex((t) => t.id === overTask.id);
        if (fromIdx !== -1 && toIdx !== -1) return arrayMove(updated, fromIdx, toIdx);
      }
      return updated;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active } = event;
    setActiveId(null);

    const moved = localTasks.find((t) => t.id === active.id);
    if (!moved) return;

    // Calculate fractional position from neighbors in the new column order
    const colTasks = localTasks
      .filter((t) => t.stage === moved.stage)
      .sort((a, b) => {
        // use the localTasks array order as the sort key
        const ai = localTasks.findIndex((x) => x.id === a.id);
        const bi = localTasks.findIndex((x) => x.id === b.id);
        return ai - bi;
      });

    const idx = colTasks.findIndex((t) => t.id === moved.id);
    const prevPos = colTasks[idx - 1] ? (tasks.find((t) => t.id === colTasks[idx - 1].id)?.position ?? 0) : 0;
    const nextPos = colTasks[idx + 1] ? (tasks.find((t) => t.id === colTasks[idx + 1].id)?.position ?? prevPos + 2000) : prevPos + 2000;
    const newPosition = (prevPos + nextPos) / 2;

    _updateTask(moved.id, { stage: moved.stage, position: newPosition });
  };

  const openCreate = (stage: Task["stage"]) => {
    setEditingTask(null);
    setDefaultStage(stage);
    setModalOpen(true);
  };

  const openEdit = (task: Task) => {
    setEditingTask(task);
    setModalOpen(true);
  };

  const handleSave = async (fields: any) => {
    if (editingTask) {
      await _updateTask(editingTask.id, fields);
    } else {
      const stageTasks = tasks.filter((t) => t.stage === fields.stage);
      const maxPos = stageTasks.length > 0 ? Math.max(...stageTasks.map((t) => t.position)) : 0;
      await _createTask({ ...fields, position: maxPos + 1000 });
    }
    setModalOpen(false);
  };

  const handleDuplicate = async () => {
    if (!editingTask) return;
    const stageTasks = tasks.filter((t) => t.stage === editingTask.stage);
    const maxPos = stageTasks.length > 0 ? Math.max(...stageTasks.map((t) => t.position)) : 0;
    await _createTask({
      title: `Copy of ${editingTask.title}`,
      description: editingTask.description,
      stage: editingTask.stage,
      priority: editingTask.priority,
      dueDate: editingTask.dueDate,
      projectId: editingTask.projectId,
      assigneeId: editingTask.assigneeId,
      position: maxPos + 1000,
    });
    setModalOpen(false);
  };

  const handleDeleteProject = (id: number) => {
    setConfirmDialog({
      message: "Archive this project? It will be hidden but can be restored from \"Show Archived Projects\".",
      onConfirm: async () => {
        await _deleteProject(id);
        setProjectFilter((prev) => prev.filter((x) => x !== id));
        setConfirmDialog(null);
      },
    });
  };

  const handleDelete = async () => {
    if (!editingTask) return;
    await _deleteTask(editingTask.id);
    setModalOpen(false);
  };

  const handleArchive = async () => {
    if (!editingTask) return;
    await _archiveTask(editingTask.id);
    setModalOpen(false);
  };

  const handleUnarchiveTask = async () => {
    if (!editingTask) return;
    await _updateTask(editingTask.id, { archived: false });
    setModalOpen(false);
  };

  const handleUnarchiveProject = async (id: number) => {
    await _unarchiveProject(id);
  };

  const startRename = (project: Project) => {
    setRenamingProject(project);
    setRenameValue(project.name);
  };

  const handleRename = async () => {
    if (!renamingProject || !renameValue.trim()) return;
    await _renameProject(renamingProject.id, renameValue.trim());
    setRenamingProject(null);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    await _createProject(newProjectName.trim());
    setNewProjectName("");
    setNewProjectOpen(false);
  };

  const toggleProjectFilter = (id: number) => {
    setProjectFilter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleStageFilter = (id: Task["stage"]) => {
    setStageFilter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3 }}>
        <PageTitle>Task Board</PageTitle>
        <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={() => openCreate("todo")}
            sx={{ whiteSpace: "nowrap" }}
          >
            New Task
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={() => setNewProjectOpen(true)}
            sx={{ whiteSpace: "nowrap" }}
          >
            New Project
          </Button>
        </Box>
      </Box>

      {/* Filter bar */}
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          p: 2,
          mb: 3,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {/* Search */}
        <TextField
          size="small"
          placeholder="Search tasks…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 280, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.85rem" } }}
        />

        {/* Project filters */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.8, minWidth: 60 }}>
            Projects
          </Typography>
          <Chip
            label="All"
            size="small"
            onClick={() => setProjectFilter([])}
            sx={allChipSx(projectFilter.length === 0, "#64748b")}
          />
          {projects.filter((p) => !p.archived).map((p) => {
            const count = taskCountByProject[p.id] || 0;
            const active = projectFilter.includes(p.id);
            const color = projectColor(p.id);
            return (
              <Box key={p.id} sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, "&:hover .proj-edit": { opacity: 1 } }}>
                <Chip
                  label={`${p.name} · ${count}`}
                  size="small"
                  onClick={() => toggleProjectFilter(p.id)}
                  onDelete={() => handleDeleteProject(p.id)}
                  sx={filterChipSx(active, color)}
                />
                <IconButton
                  className="proj-edit"
                  size="small"
                  onClick={() => startRename(p)}
                  sx={{ opacity: 0, p: 0.3, transition: "opacity 0.15s", color: "#94a3b8", "&:hover": { color: "#475569" } }}
                >
                  <EditIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>
            );
          })}
          {showArchivedProjects && projects.filter((p) => p.archived).map((p) => (
            <Chip
              key={p.id}
              label={p.name}
              size="small"
              onClick={() => handleUnarchiveProject(p.id)}
              onDelete={() => handleUnarchiveProject(p.id)}
              deleteIcon={<RestoreIcon />}
              sx={{ ...filterChipSx(false, "#94a3b8"), opacity: 0.55 }}
            />
          ))}
          <Box sx={{ ml: "auto" }}>
            <Chip
              label="Show Archived Projects"
              size="small"
              onClick={() => setShowArchivedProjects((v) => !v)}
              sx={filterChipSx(showArchivedProjects, "#f59e0b")}
            />
          </Box>
        </Box>

        {/* Stage filters */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.8, minWidth: 60 }}>
            Stages
          </Typography>
          <Chip
            label="All"
            size="small"
            onClick={() => setStageFilter([])}
            sx={allChipSx(stageFilter.length === 0, "#64748b")}
          />
          {STAGES.map((s) => (
            <Chip
              key={s.id}
              label={s.label}
              size="small"
              onClick={() => toggleStageFilter(s.id)}
              sx={filterChipSx(stageFilter.includes(s.id), STAGE_COLORS[s.id])}
            />
          ))}
          <Box sx={{ ml: "auto" }}>
            <Chip
              label="Show Archived Tasks"
              size="small"
              onClick={() => setShowArchived((v) => !v)}
              sx={filterChipSx(showArchived, "#f59e0b")}
            />
          </Box>
        </Box>
      </Box>

      {/* Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start", overflowX: "auto", pb: 2 }}>
          {STAGES.map((stage) => {
            const columnTasks = tasksForStage(stage.id);
            const color = STAGE_COLORS[stage.id];
            const bg = STAGE_BG[stage.id];

            return (
              <Box
                key={stage.id}
                sx={{
                  minWidth: 285,
                  flex: "0 0 285px",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: color + "40",
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: "calc(100vh - 280px)",
                }}
              >
                {/* Column header */}
                <Box
                  sx={{
                    backgroundColor: color,
                    px: 2,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#fff", letterSpacing: 0.3 }}>
                      {stage.label}
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.25)",
                        borderRadius: 10,
                        px: 0.9,
                        py: 0.1,
                        minWidth: 22,
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#fff" }}>
                        {columnTasks.length}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => openCreate(stage.id)}
                    sx={{ minWidth: 0, p: 0.4, color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" } }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                  </Button>
                </Box>

                {/* Cards area */}
                <Box sx={{ backgroundColor: bg, p: 1.5, flex: 1, overflowY: "auto" }}>
                  <DroppableColumn id={stage.id}>
                    <SortableContext
                      items={columnTasks.map((t) => t.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {columnTasks.map((task) => (
                        <TaskCard key={task.id} task={task} onClick={() => openEdit(task)} />
                      ))}
                    </SortableContext>

                    {columnTasks.length === 0 && (
                      <Box
                        sx={{
                          border: `2px dashed ${color}40`,
                          borderRadius: 1.5,
                          p: 2.5,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="caption" sx={{ color: color + "99", fontWeight: 500 }}>
                          Drop here
                        </Typography>
                      </Box>
                    )}
                  </DroppableColumn>
                </Box>
              </Box>
            );
          })}
        </Box>

        <DragOverlay dropAnimation={null}>
          {activeTask && (
            <Box sx={{ transform: "rotate(2deg)", opacity: 0.95 }}>
              <TaskCard task={activeTask} onClick={() => {}} />
            </Box>
          )}
        </DragOverlay>
      </DndContext>

      <TaskModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onDelete={editingTask ? handleDelete : undefined}
        onArchive={editingTask && !editingTask.archived ? handleArchive : undefined}
        onUnarchive={editingTask?.archived ? handleUnarchiveTask : undefined}
        onDuplicate={editingTask ? handleDuplicate : undefined}
        task={editingTask}
        projects={projects}
        users={users}
        defaultStage={defaultStage}
      />

      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Confirm</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#475569" }}>
            {confirmDialog?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
          <Button onClick={confirmDialog?.onConfirm} variant="contained" color="warning">
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!renamingProject} onClose={() => setRenamingProject(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Project</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField
            label="Project Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            fullWidth
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleRename()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenamingProject(null)}>Cancel</Button>
          <Button onClick={handleRename} variant="contained" disabled={!renameValue.trim()}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newProjectOpen} onClose={() => setNewProjectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Project</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField
            label="Project Name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            fullWidth
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleCreateProject()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewProjectOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateProject} variant="contained" disabled={!newProjectName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Chip style helpers
function filterChipSx(active: boolean, color: string) {
  return {
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 26,
    backgroundColor: active ? color : "#f1f5f9",
    color: active ? "#fff" : "#64748b",
    border: `1.5px solid ${active ? color : "#cbd5e1"}`,
    "& .MuiChip-label": { px: 1.25 },
    "&:hover": {
      backgroundColor: active ? color : "#e2e8f0",
    },
    transition: "all 0.15s ease",
  };
}

function allChipSx(active: boolean, color: string) {
  return {
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.75rem",
    height: 26,
    backgroundColor: active ? color : "#f1f5f9",
    color: active ? "#fff" : "#94a3b8",
    border: `1.5px solid ${active ? color : "#cbd5e1"}`,
    "& .MuiChip-label": { px: 1.25 },
    "&:hover": {
      backgroundColor: active ? color : "#e2e8f0",
    },
    transition: "all 0.15s ease",
  };
}

const mapStateToProps = (state: StoreState) => ({
  tasks: state.taskBoard.tasks,
  projects: state.taskBoard.projects,
  users: state.admin.users,
});

export default connect(mapStateToProps, {
  fetchTaskBoard,
  createTask,
  updateTask,
  deleteTask,
  archiveTask,
  createProject,
  deleteProject,
  unarchiveProject,
  renameProject,
})(TaskBoardPage);
